import type { ShipmentStatus } from '@erp/data-access/interfaces';
import type {
  IDeliveryProvider,
  DeliveryParcelInput,
  DeliveryParcelResult,
  DeliveryStatusUpdate,
  DeliveryWebhookPayload,
  DeliveryProviderRuntimeConfig,
} from '../interfaces';

/** In-memory mock provider used during P1 development.
 *  Mirrors Intigo's status enum so the UI can be wired once and switch to the
 *  real Intigo adapter without changes. */
export class MockDeliveryProvider implements IDeliveryProvider {
  readonly code = 'mock';
  readonly displayName = 'Mock (dev)';

  constructor(_config?: DeliveryProviderRuntimeConfig) {
    // config unused — mock keeps state in memory
  }

  async createParcel(input: DeliveryParcelInput): Promise<DeliveryParcelResult> {
    const nid = `MOCK-${Math.floor(Math.random() * 1_000_000_000)}`;
    return {
      providerExternalId: nid,
      raw: { success: true, nid, echo: input },
    };
  }

  async createParcelBatch(inputs: DeliveryParcelInput[]): Promise<DeliveryParcelResult[]> {
    return Promise.all(inputs.map(i => this.createParcel(i)));
  }

  async getStatus(providerExternalIds: string[]): Promise<DeliveryStatusUpdate[]> {
    // Deterministic pseudo-progression based on the id, so repeated calls
    // surface a believable lifecycle without random churn.
    return providerExternalIds.map(nid => {
      const tail = parseInt(nid.replace(/\D/g, '').slice(-3) || '0', 10);
      const stages = [1000, 2000, 2004, 3100, 4000, 5000];
      const stageIdx = tail % stages.length;
      const code = stages[stageIdx]!;
      return {
        providerExternalId: nid,
        statusCode: code,
        statusLabel: STATUS_LABELS[code] ?? 'Unknown',
        occurredAt: new Date().toISOString(),
      };
    });
  }

  async cancelParcel(_providerExternalId: string): Promise<void> {
    // no-op
  }

  mapStatusToShipment(statusCode: number): ShipmentStatus {
    return mapIntigoStatusToShipment(statusCode);
  }

  isFinalStatus(statusCode: number): boolean {
    return statusCode === 5000 || (statusCode >= 6000 && statusCode <= 6999) || (statusCode >= 9000 && statusCode <= 9004);
  }

  async generateLabelPdf(input: {
    shipments: Array<{
      providerExternalId: string;
      recipientName: string;
      phone: string;
      address: string;
      cityName: string;
      codAmount: number;
      orderRef: string;
      packageSize: number;
    }>;
    companyName: string;
  }): Promise<Uint8Array> {
    // Mock: return a tiny PDF-like header so the print pipeline can run end-to-end.
    // Real Intigo adapter will use pdfmake.
    const text = `MOCK LABEL\n${input.companyName}\n\n` +
      input.shipments.map(s => `${s.providerExternalId} → ${s.recipientName}, ${s.address}, ${s.cityName} (COD ${s.codAmount} DT)`).join('\n');
    const encoder = new TextEncoder();
    return encoder.encode(text);
  }

  async verifyWebhookSignature(_raw: string, _header: string, _secret: string): Promise<boolean> {
    return true; // mock accepts any signature
  }

  parseWebhook(rawBody: string): DeliveryWebhookPayload {
    const json = JSON.parse(rawBody) as {
      event?: string;
      event_type?: string;
      timestamp?: string;
      data?: { nid?: string; old_status?: number; new_status?: number; status_label?: string };
    };
    const data = json.data ?? {};
    return {
      event: json.event ?? 'parcel.status_changed',
      eventType: json.event_type ?? 'unknown',
      providerExternalId: data.nid ?? '',
      oldStatusCode: data.old_status ?? null,
      newStatusCode: data.new_status ?? 0,
      statusLabel: data.status_label ?? '',
      timestamp: json.timestamp ?? new Date().toISOString(),
    };
  }
}

// ---- Shared with Intigo adapter (status code mapping is identical) ----

export const STATUS_LABELS: Record<number, string> = {
  1000: 'Pickup pending',
  1100: 'Cancelled at pickup',
  2000: 'Pending delivery',
  2004: 'In depot',
  3100: 'In transit',
  4000: 'Out for delivery',
  5000: 'Delivered',
  6000: 'Return preparation',
  6001: 'Return ready',
  6500: 'Return exchange',
  6900: 'Returned received',
  9000: 'Cancelled',
  9001: 'Cancelled',
  9002: 'Cancelled',
  9003: 'Cancelled',
  9004: 'Cancelled',
  99: 'Rerouting',
};

export function mapIntigoStatusToShipment(statusCode: number): ShipmentStatus {
  if (statusCode === 5000) return 'delivered';
  if (statusCode >= 9000 && statusCode <= 9004) return 'cancelled';
  if (statusCode === 1100) return 'cancelled';
  if (statusCode === 6500) return 'returned';
  if (statusCode >= 6000 && statusCode <= 6999) return 'returned';
  if (statusCode === 4000) return 'in_transit';
  if (statusCode === 3100 || statusCode === 2000 || statusCode === 2004 || statusCode === 99) return 'dispatched';
  if (statusCode === 1000) return 'pending';
  return 'pending';
}
